import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import {
    CalendarComponent,
    CalendarSelectedDates,
} from 'src/app/controls/calendar/calendar.component';
import {
    createDateFromString,
    createDateString,
    transformNgbDateToDayJs,
} from 'src/app/controls/calendar/calendar.utils';
import { OccupancySelection } from '../search-occupancy/search-occupancy.model';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { SearchOccupancyComponent } from '../search-occupancy/search-occupancy.component';
import { NavigationEnd, Params, Router } from '@angular/router';
import { removeQueryParams } from 'src/app/utilities/url.utils';
import {
    LocationOption,
    SearchLocationComponent,
} from '../search-location/search-location.component';
import { RouteParamsService } from 'src/app/services/route-params.service';
import * as dayjs from 'dayjs';
import {
    compileOccupancyQueryString,
    compileOccupancyString,
} from '../search-occupancy/search-occupancy.utils';
import { CartButtonService } from '../cart-button/cart-button.service';
import { PopupOffcanvasComponent } from '@app/controls/popup-offcanvas/popup-offcanvas.component';
import { ScreenScrollService } from 'src/app/services/screen-scroll.service';
import * as Sentry from '@sentry/angular-ivy';

@Component({
    selector: 'md-call-to-action',
    templateUrl: './call-to-action.component.html',
    styleUrls: ['./call-to-action.component.scss'],
})
export class SearchDatesComponent implements OnInit {
    fromDate: NgbDate;
    toDate: NgbDate;
    formattedFromDate = 'Fri, 22 Mar';
    formattedToDate = 'Sat, 23 Mar';
    occupancyString = 'Add Guests';
    formattedMobileDates = '';
    selectedOccupancy: OccupancySelection;

    singleDate: NgbDate;
    formattedSingleDate = 'Fri, 22 Mar';
    locationString = 'Add Location';
    selectedLocation: LocationOption;

    isScrolled: boolean;
    isOnDayPackagePage: boolean;

    sectionId: number;

    @ViewChild('searchOccupancy') desktopOccupancy: SearchOccupancyComponent;
    @ViewChild('searchLocation') desktopLocation: SearchLocationComponent;
    @ViewChild('calendarSearchDates') calendarMariBnb: CalendarComponent;
    @ViewChild('calendarSingleSearchDates')
    calendarSingleSearchDates: CalendarComponent;
    @ViewChild('mobileCTAOffcanvas')
    mobileCTAOffcanvas: PopupOffcanvasComponent;

    constructor(
        public cartButtonService: CartButtonService,
        private router: Router,
        private elementRef: ElementRef,
        private routeParamsService: RouteParamsService,
        private location: Location,
        private screenScrollService: ScreenScrollService
    ) {
        this.clearAll = this.clearAll.bind(this);
        this.onMobileOccupancySearchClick =
            this.onMobileOccupancySearchClick.bind(this);
    }

    ngOnInit(): void {
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                const urlNoQueryParams = removeQueryParams(
                    event.urlAfterRedirects
                );
                // TODO: this most likely can be done better through the router
                this.showSearchBasedOnPage(urlNoQueryParams);
            }
        });
        this.routeParamsService.sectionId$.subscribe((sectionId) => {
            this.sectionId = sectionId;
        });
    }

    onMobileCTAOffcanvasOpen() {
        this.mobileCTAOffcanvas.open();
    }

    openCalendar() {
        if (this.isOnDayPackagePage) {
            this.calendarSingleSearchDates.openCalendar();
        } else {
            this.calendarMariBnb.openCalendar();
        }
    }

    onWhoWhereClicked() {
        if (this.isOnDayPackagePage) {
            this.onLocationClicked();
        } else {
            this.onOccupancyClicked();
        }
    }

    onOccupancyClicked(): void {
        if (!this.fromDate || !this.toDate) {
            this.calendarMariBnb.openCalendar();
        } else {
            this.desktopOccupancy.openOccupancy();
        }
    }

    onLocationClicked(): void {
        if (!this.singleDate) {
            this.calendarSingleSearchDates.openCalendar();
        } else {
            this.desktopLocation.openLocation();
        }
    }

    onDateRangeSelected(selectedDates: CalendarSelectedDates): void {
        const { fromDate, toDate } = selectedDates;

        if (!fromDate || !toDate) {
            return;
        }

        this.fromDate = fromDate;
        this.toDate = toDate;
        this.compileSelectionToShow();

        this.calendarMariBnb.closeCalendar();
        if (!this.selectedOccupancy) {
            setTimeout(() => {
                this.desktopOccupancy.openOccupancy();
            }, 0);
        }
    }

    onSingleDateSelected(selectedDates: CalendarSelectedDates): void {
        const { singleDate } = selectedDates;

        if (!singleDate) {
            return;
        }

        this.singleDate = singleDate;
        this.compileSelectionToShow();

        this.calendarSingleSearchDates.closeCalendar();
        if (!this.selectedLocation) {
            setTimeout(() => {
                this.desktopLocation.openLocation();
            }, 0);
        }
    }

    onLocationChanged(location: LocationOption): void {
        this.selectedLocation = location;
        this.compileSelectionToShow();
    }

    onOccupancyChanged(occupancySelection: OccupancySelection): void {
        this.selectedOccupancy = occupancySelection;
        this.compileSelectionToShow();
        this.search();
    }

    onMobileOccupancySearchClick(): void {
        this.search();
        this.mobileCTAOffcanvas.close();
    }

    search() {
        const notAllConfigured = this.openPopupsIfNotAllConfigured();
        if (notAllConfigured) {
            return;
        }
        const urlToNavigate = this.sectionId
            ? `/search/${this.sectionId}`
            : '/search';
        this.router.navigate([urlToNavigate], {
            queryParams: this.compileSearchQueryParameters(),
        });
    }

    private openPopupsIfNotAllConfigured(): boolean {
        if (this.isOnDayPackagePage) {
            return false;
        }

        if (!this.fromDate || !this.toDate) {
            this.calendarMariBnb.openCalendar();
            return true;
        }
        if (!this.selectedOccupancy) {
            this.desktopOccupancy.openOccupancy();
            return true;
        }
    }

    goBack() {
        this.location.back();
    }

    clearAll() {
        this.setInitialData(this.isOnDayPackagePage);
    }

    private compileSelectionToShow() {
        this.formattedFromDate = this.getFormattedDate(this.fromDate);
        this.formattedToDate = this.getFormattedDate(this.toDate);
        this.occupancyString = compileOccupancyString(this.selectedOccupancy);
        this.formattedMobileDates = this.composeFormattedMobileSelection();
        this.formattedSingleDate = this.getFormattedDate(this.singleDate);
        this.locationString = this.selectedLocation?.name;
    }

    private compileSearchQueryParameters(): Params {
        if (this.singleDate) {
            const fromDate = createDateString(this.singleDate);
            const toDate = createDateString(this.singleDate);
            const filterTypeId = this.selectedLocation?.filterTypeId;
            const filterId = this.selectedLocation?.filterId;
            const filterQueryParamValue = filterId
                ? `${filterTypeId}-${filterId}`
                : null;

            return {
                fromDate,
                toDate,
                filters: filterQueryParamValue,
            };
        }

        try {
            Sentry.addBreadcrumb({
                category: 'call-to-action',
                message: 'Search stays clicked',
                level: 'info',
            });
            Sentry.captureEvent({
                message: 'Search stays clicked',
                level: 'info',
                tags: {
                    action: 'search_clicked',
                    fromDate: createDateString(this.fromDate),
                    toDate: createDateString(this.toDate),
                    occupancy: JSON.stringify(this.selectedOccupancy),
                },
            });
        } catch (error) {
            // do nothing
        }
        const fromDate = createDateString(this.fromDate);
        const toDate = createDateString(this.toDate);
        const occupancy = compileOccupancyQueryString(this.selectedOccupancy);

        return {
            fromDate,
            toDate,
            occupancy,
        };
    }

    private setInitialData(singleDate: boolean): void {
        if (singleDate) {
            this.setInitialDataForSingleDate();
        } else {
            this.setInitialDataForMultiDate();
        }
    }
    private setInitialDataForSingleDate(): void {
        this.resetDates();
        const today = new Date();
        const firstFriday = new Date();
        firstFriday.setDate(today.getDate() + ((5 - today.getDay() + 7) % 7));
        this.singleDate = new NgbDate(
            firstFriday.getFullYear(),
            firstFriday.getMonth() + 1,
            firstFriday.getDate()
        );
        this.formattedSingleDate = this.getFormattedDate(this.singleDate);
        this.selectedLocation = undefined;
    }

    private setInitialDataForMultiDate(): void {
        this.resetDates();

        const [firstFriday, firstSunday] = this.getNextFridayAndSunday();

        this.fromDate = new NgbDate(
            firstFriday.getFullYear(),
            firstFriday.getMonth() + 1,
            firstFriday.getDate()
        );
        this.toDate = new NgbDate(
            firstSunday.getFullYear(),
            firstSunday.getMonth() + 1,
            firstSunday.getDate()
        );
        this.selectedOccupancy = undefined;

        this.compileSelectionToShow();
    }

    private getNextFridayAndSunday(): [Date, Date] {
        const today = new Date();
        if (today.getDay() === 5 || today.getDay() === 6) {
            today.setDate(today.getDate() + 3);
        }
        const firstFriday = new Date();
        firstFriday.setDate(today.getDate() + ((5 - today.getDay() + 7) % 7));
        const firstSunday = dayjs(firstFriday).add(2, 'day').toDate();

        return [firstFriday, firstSunday];
    }

    private resetDates(): void {
        this.fromDate = undefined;
        this.toDate = undefined;
        this.formattedFromDate = '';
        this.formattedToDate = '';
        this.occupancyString = 'Add Guests';
        this.selectedOccupancy = undefined;
        this.singleDate = undefined;
        this.formattedSingleDate = '';
        this.locationString = 'Add Location';
        this.formattedMobileDates = '';
        this.selectedLocation = undefined;
    }

    private getFormattedDate(date: NgbDate): string {
        if (!date) {
            return '-';
        }
        const actualDate = transformNgbDateToDayJs(date);
        return actualDate.format('ddd, DD MMM YYYY');
    }

    private composeFormattedMobileSelection(): string {
        if (!this.fromDate || !this.toDate) {
            return '';
        }

        const fromDateDayJs = transformNgbDateToDayJs(this.fromDate);
        const toDateDayJs = transformNgbDateToDayJs(this.toDate);
        return `${fromDateDayJs.format('DD')} - ${toDateDayJs.format(
            'DD MMM YYYY'
        )}`;
    }

    private showSearchBasedOnPage(urlNoQueryParams: string): void {
        const isHomePage = urlNoQueryParams === '/';
        const isCategoryPage = urlNoQueryParams.includes('/plp/');
        const showCTAOnDealPage =
            urlNoQueryParams.includes('/3') ||
            urlNoQueryParams.includes('/8') ||
            urlNoQueryParams.includes('/9');
        const isDayPackagePage = urlNoQueryParams.includes('/8');
        const isSearchPage = urlNoQueryParams.includes('/search');
        const showSearch =
            isSearchPage || isHomePage || (isCategoryPage && showCTAOnDealPage);

        this.elementRef.nativeElement.classList.toggle(
            'search-hidden',
            !showSearch
        );

        if (showSearch) {
            if (isSearchPage) {
                this.setFormattedFromAndToDatesFromQueryString();
            } else if (this.isOnDayPackagePage !== isDayPackagePage) {
                this.isOnDayPackagePage = isDayPackagePage;
                this.setInitialData(isDayPackagePage);
            }
        }

        const routerContainer =
            document.getElementsByClassName('routerContainer')[0];
        if (routerContainer) {
            routerContainer.classList.toggle(
                'a-router-container--no-search',
                !showSearch
            );
        }
    }

    private setFormattedFromAndToDatesFromQueryString(): void {
        const { fromDate, toDate, occupancyAsString } =
            this.getToAndFromDateFromQueryParams();
        if (fromDate && toDate) {
            this.fromDate = createDateFromString(fromDate);
            this.toDate = createDateFromString(toDate);
            if (occupancyAsString) {
                this.selectedOccupancy =
                    this.parseOccupancyStringAndCleanUpInvalidChildAges(
                        occupancyAsString
                    );
            }
            this.compileSelectionToShow();
        }
    }

    private parseOccupancyStringAndCleanUpInvalidChildAges(
        occupancyString: string
    ): OccupancySelection {
        const occupancy: OccupancySelection = JSON.parse(occupancyString);
        const children = occupancy.children.filter((age) => age >= 0);
        return { ...occupancy, children };
    }

    private getToAndFromDateFromQueryParams(): {
        fromDate: string;
        toDate: string;
        occupancyAsString: string;
    } {
        const fromDate = this.router.parseUrl(this.router.url).queryParams[
            'fromDate'
        ];
        const toDate = this.router.parseUrl(this.router.url).queryParams[
            'toDate'
        ];
        const occupancyAsString = this.router.parseUrl(this.router.url)
            .queryParams['occupancy'];

        return { fromDate, toDate, occupancyAsString };
    }
}
